import { cloneDeep } from 'lodash';
import { Row, Select, Space } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';

import { Store } from '../../../state/store.interfaces';
import Icon from '../../../common/components/Icon/Icon';
import { IntegratedPond, Module, Tank } from '../interfaces';
import { LrvModal } from '../../../common/components/LrvModal/LrvModal';
import { LrvTable } from '../../../common/components/LrvTable/LrvTable';
import { LrvSelect } from '../../../common/components/LrvSelect/LrvSelect';
import { LrvSelectV2 } from '../../../common/components/LrvSelectV2/LrvSelectV2';
import IconButton from '../../../common/components/buttons/IconButton';

import styles from './TankIntegrationForm.module.scss';
import * as integrationAQ1Slice from './integrationAQ1Slice';

interface Props {
  theme?: 'dark' | 'light';
}

const { Option } = Select;

export const TankIntegrationForm = (props: Props) => {
  const { theme } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { showModal, integratedPonds, isLoading, campuses, isUpdating } = useSelector((state: Store) => state.integrationAQ1);
  const { externalAccess } = useSelector((state: Store) => state.externalAccess);

  const [selectedExternalAccess, setSelectedExternalAccess] = useState<string>();

  useEffect(() => {
    if (!showModal || !externalAccess.length) {
      return;
    }

    const externalAccessId = externalAccess.find((item) => item.active)?._id;
    if (!externalAccessId) {
      return;
    }

    setSelectedExternalAccess(externalAccessId);
    dispatch(integrationAQ1Slice.fetchIntegratedAQ1Ponds({ externalAccessId }));
  }, [dispatch, showModal, externalAccess]);

  const onCloseModal = () => {
    dispatch(integrationAQ1Slice.setCampuses([]));
    dispatch(integrationAQ1Slice.setShowModal(false));
  };

  const getColumns = () => {
    const columns: ColumnProps<IntegratedPond>[] = [
      {
        title: 'AQ1',
        width: '24%',
        render: (_, record: IntegratedPond) => record.aq1.pondName,
      },
      {
        title: '',
        width: '8%',
        render: () => <Icon name='arrow-right-long' theme={theme} />,
      },
      {
        title: t('campus.campus'),
        width: '24%',
        render: (_, record: IntegratedPond, index: number) => {
          return (
            <LrvSelect
              showSearch
              theme={theme}
              value={record.larvia.campusName}
              label={t('campus.campus')}
              className={styles.select}
              suffixIcon={<Icon name='arrow-down-s'/>}
              dropdownMatchSelectWidth={false}
              onChange={(value) => {
                const campus = campuses.find((item) => item._id === value);
                if (!campus) {
                  return;
                }

                const editedTanksCopy = cloneDeep(integratedPonds);
                editedTanksCopy[index].larvia.campusId = campus?._id;
                editedTanksCopy[index].larvia.campusName = campus?.name;
                editedTanksCopy[index].larvia.moduleId = undefined;
                editedTanksCopy[index].larvia.moduleName = undefined;
                editedTanksCopy[index].larvia.tankId = undefined;
                editedTanksCopy[index].larvia.tankName = undefined;

                dispatch(integrationAQ1Slice.setIntegratedPonds(editedTanksCopy));
              }}
            >
              {campuses.map((item) =>
                <Option key={item._id} value={item._id}>{item.name}</Option>
              )}
            </LrvSelect>
          );
        },
      },
      {
        title: t('campus.module'),
        width: '24%',
        render: (_, record: IntegratedPond, index: number) => {
          return (
            <SelectorModule
              integratedPonds={integratedPonds}
              integratedPond={record}
              index={index}
            />
          );
        },
      },
      {
        title: t('campus.containerTypes.POOL'),
        width: '24%',
        render: (_, record: IntegratedPond, index: number) => {
          return (
            <SelectorPond
              integratedPonds={integratedPonds}
              integratedPond={record}
              index={index}
            />
          );
        },
      },
      {
        width: '6%',
        render: (_, record: IntegratedPond, index: number) => {
          return (
            <IconButton
              theme={theme}
              className={styles.buttonDelete}
              icon={<Icon name='delete-bin' type='line' theme={theme} className={styles.icon} />}
              onClick={() => {
                const editedTanksCopy = cloneDeep(integratedPonds);
                editedTanksCopy[index].larvia.campusId = undefined;
                editedTanksCopy[index].larvia.campusName = undefined;
                editedTanksCopy[index].larvia.moduleId = undefined;
                editedTanksCopy[index].larvia.moduleName = undefined;
                editedTanksCopy[index].larvia.tankId = undefined;
                editedTanksCopy[index].larvia.tankName = undefined;

                dispatch(integrationAQ1Slice.setIntegratedPonds(editedTanksCopy));
              }}
            />
          );
        },
      },
    ];

    return columns;
  };

  const renderExternalAccess = () => {
    return (
      <LrvSelectV2
        showSearch
        theme={theme}
        value={selectedExternalAccess}
        label={t('integrations.title')}
        className={styles.select}
        onChange={(value) => {
          setSelectedExternalAccess(value);
          dispatch(integrationAQ1Slice.fetchIntegratedAQ1Ponds({ externalAccessId: value }));

          const filteredIntegratedPonds = integratedPonds.filter((integratedPond) => !!integratedPond.larvia.tankId);

          if (filteredIntegratedPonds.length > 0) {
            dispatch(integrationAQ1Slice.updateIntegratedAQ1Ponds({ integratedPonds: filteredIntegratedPonds }));
          }
        }}
      >
        {externalAccess.filter((item) => item.active).map((item) =>
          <Option key={item._id} value={item._id}>{item.apiConfig.name}</Option>
        )}
      </LrvSelectV2>
    );
  };

  const onSaveIntegratedAQ1Ponds = () => {
    if (integratedPonds.length > 0) {
      dispatch(integrationAQ1Slice.updateIntegratedAQ1Ponds({ integratedPonds, onSuccess: onCloseModal }));
    }
  };

  return (
    <LrvModal
      theme={theme}
      title={t('integrations.poolMapping.title')}
      open={showModal}
      closeIcon={<Icon id='close_modal' name='close' />}
      okText={t('actions.save')}
      onOk={onSaveIntegratedAQ1Ponds}
      onCancel={onCloseModal}
      okButtonProps={{ loading: isUpdating }}
      className={styles.container}
      width={860}
    >
      <Space direction='vertical'>
        <Row justify='space-between'>
          {renderExternalAccess()}
        </Row>

        <LrvTable
          theme={theme}
          columns={getColumns()}
          dataSource={integratedPonds}
          size='small'
          pagination={false}
          loading={isLoading}
          scroll={{ y: 472 }}
        />
      </Space>
    </LrvModal>
  );
};

const SelectorModule = (props: { integratedPond: IntegratedPond; integratedPonds: IntegratedPond[]; index: number; }) => {
  const { integratedPond, integratedPonds, index } = props;

  const dispatch = useDispatch();

  const [modules, setModules] = useState<Module[]>([]);

  const fetchModules = useCallback(async () => {
    if (!integratedPond.larvia.campusId) {
      return;
    }

    try {
      const modules = await integrationAQ1Slice.fetchModules({ campusId: integratedPond.larvia.campusId });
      setModules(modules);
    } catch (error) {
      console.log(error?.response);
    }
  }, [integratedPond.larvia.campusId]);

  useEffect(() => {
    fetchModules();

    return (() => {
      setModules([]);
    });
  }, [fetchModules]);

  return (
    <LrvSelect
      theme='light'
      value={integratedPond.larvia.moduleName || undefined}
      className={styles.input}
      showSearch
      suffixIcon={<Icon name='arrow-down-s'/>}
      dropdownMatchSelectWidth={false}
      disabled={!integratedPond.larvia.campusId}
      onChange={value => {
        const module = modules.find((item) => item._id === value);
        if (!module) {
          return;
        }

        const editedTanksCopy = cloneDeep(integratedPonds);
        editedTanksCopy[index].larvia.moduleId = module?._id;
        editedTanksCopy[index].larvia.moduleName = module?.name;
        editedTanksCopy[index].larvia.tankId = undefined;
        editedTanksCopy[index].larvia.tankName = undefined;

        dispatch(integrationAQ1Slice.setIntegratedPonds(editedTanksCopy));
      }}
    >
      {modules.map((module) =>
        <Option key={module._id} value={module._id}>{module.name}</Option>
      )}
    </LrvSelect>
  );
};

const SelectorPond = (props: { integratedPond: IntegratedPond; integratedPonds: IntegratedPond[]; index: number; }) => {
  const { integratedPond, integratedPonds, index } = props;

  const dispatch = useDispatch();

  const [ponds, setPonds] = useState<Tank[]>([]);

  const fetchPonds = useCallback(async () => {
    if (!integratedPond.larvia.campusId || !integratedPond.larvia.moduleId) {
      return;
    }

    try {
      const modules = await integrationAQ1Slice.fetchTanks({ campusId: integratedPond.larvia.campusId, moduleId: integratedPond.larvia.moduleId });
      setPonds(modules);
    } catch (error) {
      console.log(error?.response);
    }
  }, [integratedPond.larvia.moduleId]);

  useEffect(() => {
    fetchPonds();

    return (() => {
      setPonds([]);
    });
  }, [fetchPonds]);

  const getFilteredTanks = () => {
    const tankIds = integratedPonds.map((integratedPond) => integratedPond.larvia.tankId);
    const filteredTanks = ponds.filter((tank) => !tankIds.includes(tank._id));
    return filteredTanks;
  };

  return (
    <LrvSelect
      theme='light'
      value={integratedPond.larvia.tankName || undefined}
      className={styles.input}
      showSearch
      disabled={!integratedPond.larvia.moduleId}
      dropdownMatchSelectWidth={false}
      suffixIcon={<Icon name='arrow-down-s'/>}
      onChange={value => {
        const pond = ponds.find((item) => item._id === value);
        if (!pond) {
          return;
        }

        const editedTanksCopy = cloneDeep(integratedPonds);
        editedTanksCopy[index].larvia.tankId = pond._id;
        editedTanksCopy[index].larvia.tankName = pond.name;

        dispatch(integrationAQ1Slice.setIntegratedPonds(editedTanksCopy));
      }}
    >
      {getFilteredTanks().map((pond) =>
        <Option key={pond._id} value={pond._id}>{pond.name}</Option>
      )}
    </LrvSelect>
  );
};
